import type { ReactElement, ReactNode } from 'react'
import { isTrue, type TeamLevel, type TinyTeam, type WithTeamId } from '@terros/common'
import { Text } from '@mantine/core'
import { getInitialism, useProfile, getLevelName, useTinyTeam } from '@front/model'
import { type InfoFetcherProps } from './types'
import { LoadingEntityInfo, PrefetchedEntityInfo } from './EntityInfo'
import { EntityHoverCard, type EntityMetadataRow } from './EntityHoverCard'

type TeamInfoProps = InfoFetcherProps & WithTeamId

export const TeamInfo = ({ teamId, linked, ...avatarProps }: TeamInfoProps): ReactNode => {
  const { data, isLoading, error } = useTinyTeam(teamId)

  if (error) return <Text className='text-red-400'>{error}</Text>
  if (isLoading || !data) return <LoadingEntityInfo {...avatarProps} />

  return <PrefetchedTeamInfo linked={linked} {...avatarProps} team={data} />
}

type TeamInfoPrefetchedInput = InfoFetcherProps & {
  team: TinyTeam & {
    level?: TeamLevel
  }
  companyName?: string
  /** Drop the "Sub Agent:" style prefix from the name, e.g. when the sentence around it already names the level */
  hideLevel?: boolean
}

export const PrefetchedTeamInfo = (props: TeamInfoPrefetchedInput): ReactElement => {
  const { company } = useProfile()

  const { linked = true, team, hideLevel = false, ...rest } = props

  const { teamId, name } = team

  const initials = getInitialism(name)
  const levelName = team.level ? getLevelName(company, team.level) : undefined
  const displayName = levelName && !hideLevel ? `${levelName}: ${name}` : name

  const entityInfo = {
    ...rest,
    avatarBlurhash: team.avatarBlurhash,
    avatarUrl: team.avatarUrl,
    isDeleted: team.isDeleted,
    name: displayName,
    initials,
  }

  const content = <PrefetchedEntityInfo {...entityInfo} />

  if (linked) {
    const metadata: EntityMetadataRow[] = [
      { icon: 'badge', label: 'Team ID', value: teamId, copyable: true },
      {
        icon: 'tag',
        label: 'External ID',
        value: team.externalId,
        copyable: true,
      },
    ]
    if (isTrue(team.restrictVisibility)) {
      metadata.push({
        icon: 'lock',
        label: 'Visibility',
        value: 'Visibility restricted',
      })
    }
    return (
      <EntityHoverCard {...entityInfo} subtitle={levelName} name={name} href={`/team/${teamId}`} metadata={metadata}>
        {content}
      </EntityHoverCard>
    )
  }
  return content
}
